import { LuAudioLines, LuUser, LuFilm, LuBrainCircuit } from "react-icons/lu";
import { FiClock, FiUser, FiCrop } from "react-icons/fi";
import { MdAspectRatio, MdHd } from "react-icons/md";
import { IoIosRefresh } from "react-icons/io";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/shadcn/select";
import { Input } from "@/components/ui/shadcn/input";
import { Checkbox } from "@/components/ui/shadcn/checkbox";
import Button from "@/components/ui/Button";

/**
 * Loose-matched keyword → icon. Order matters: the first keyword found in the
 * normalized input name wins. New options names should grow this list.
 *
 * Distinct names observed in the AiModel collection at the time of writing:
 *   Aspect ratio, Aspect Ratio, aspect_ratio, Audio, Character Orientation,
 *   Duration, FPS, Frame, Model, Resolution, Retake Mode, Size.
 */
const OPTIONS_ICON_MAP = [
  ["aspectratio", MdAspectRatio],
  ["size", MdAspectRatio],
  ["resolution", MdHd],
  ["duration", FiClock],
  ["fps", LuFilm],
  ["retake", IoIosRefresh],
  ["orientation", FiUser],
  ["frame", FiCrop],
  ["audio", LuAudioLines],
  ["model", LuBrainCircuit],
];

/**
 * @input ('Aspect Ratio')
 * @output <MdAspectRatio className='w-3 h-3' />
 */
function getOptionsIcon(name, className = "w-3 h-3") {
  if (!name) return null;
  const normalized = String(name)
    .toLowerCase()
    .replace(/[\s_\-]/g, "");
  for (const [keyword, Icon] of OPTIONS_ICON_MAP) {
    if (normalized.includes(keyword)) return <Icon className={className} />;
  }
  return null;
}

export default function SettingInput({
  input,
  value,
  characters,
  onChange,
  onEditVoiceSettings,
}) {
  if (input.type === "options") {
    const icon = getOptionsIcon(input.name);

    return (
      <div id={`SettingInput--${input.prop}`} className="flex flex-col gap-1">
        <Select value={value} onValueChange={(v) => onChange(v)}>
          <SelectTrigger
            id={`SettingInput__trigger--${input.prop}`}
            title={input.name}
            className="text-xs rounded-sm border-0 !h-6 px-2 select-none gap-1.5"
            showChevron={false}>
            {icon && (
              <span className="text-secondary-foreground/70 shrink-0">
                {icon}
              </span>
            )}
            <SelectValue placeholder={input.name} />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {input.enum?.map((opt) => (
                <SelectItem
                  id={`SettingInput__option--${input.prop}-${opt.key}`}
                  key={opt.key}
                  value={opt.key}
                  className="text-xs">
                  {opt.value}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
    );
  }
  if (input.type === "character") {
    const selectedCharacter = characters.find((c) => c.voiceId === value);
    const showThumbnail = !!selectedCharacter?.thumbnail_url;
    return (
      <div id={`SettingInput--${input.prop}`} className="flex flex-col gap-1">
        <Select value={value} onValueChange={(v) => onChange(v)}>
          <SelectTrigger
            id={`SettingInput__trigger--${input.prop}`}
            size="xs"
            title={selectedCharacter?.name || "Character"}
            className="min-w-24 text-xs select-none px-1.5 flex items-center justify-start"
            showChevron={false}>
            {showThumbnail ? (
              <img
                src={selectedCharacter.thumbnail_url}
                alt={selectedCharacter.name}
                className="w-3 h-3 rounded-full object-cover shrink-0"
              />
            ) : (
              <span className="text-secondary-foreground/70 shrink-0">
                <LuUser className="w-3 h-3 shrink-0" />
              </span>
            )}
            <SelectValue placeholder="Character" className="text-xs">
              {selectedCharacter?.name}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {characters.map((c) => (
                <SelectItem
                  key={c.voiceId}
                  value={c.voiceId}
                  className="text-xs">
                  {c.thumbnail_url ? (
                    <img
                      src={c.thumbnail_url}
                      alt={c.name}
                      className="w-6 h-6 rounded-full object-cover"
                    />
                  ) : (
                    <LuUser className="w-6 h-6 p-1 rounded-full bg-secondary text-secondary-foreground/70" />
                  )}
                  <span className="text-xs">{c.name}</span>
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
    );
  }
  if (input.type === "number") {
    return (
      <div id={`SettingInput--${input.prop}`} className="flex flex-col gap-1">
        <span className="text-[10px] text-secondary-foreground/70 px-0.5 select-none">
          {input.name}
          {input.required && (
            <span className="text-destructive-foreground">*</span>
          )}
        </span>
        <Input
          type="number"
          min={input.min ?? -9999}
          max={input.max ?? 9999}
          step={input.step ?? 1}
          value={value ?? ""}
          onChange={(e) => onChange(parseFloat(e.target.value || "0"))}
          className="h-6 w-18 text-xs pr-0 overflow-hidden"
        />
      </div>
    );
  }
  if (input.type === "boolean") {
    return (
      <div
        id={`SettingInput--${input.prop}`}
        className="flex gap-1 items-center border rounded-sm h-6  px-2">
        <Checkbox
          id={input.prop}
          checked={!!value}
          onCheckedChange={(c) => onChange(c)}
        />
        <label
          htmlFor={input.prop}
          className="text-[10px] text-secondary-foreground/70 px-0.5 select-none">
          {input.name}
        </label>
      </div>
    );
  }
  if (input.type === "voice_settings") {
    return (
      <div id={`SettingInput--${input.prop}`} className="flex flex-col gap-1">
        <Button
          type="secondary"
          size="sm"
          className="!text-xs !h-6"
          icon={<LuAudioLines className="w-3 h-3" />}
          title={input.name}
          onClick={() => onEditVoiceSettings(value || {})}>
          {value ? "Edit" : "Settings"}
        </Button>
      </div>
    );
  }
  return null;
}
